import React from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
}

export const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
  className,
}) => {
  const selected = options.find((o) => o.value === value) || null;

  return (
    <Listbox value={value} onChange={onChange}>
      {({ open }) => (
        <div className={clsx("relative", className)}>
          <Listbox.Button
            className={clsx(
              "w-full cursor-default rounded-lg bg-white py-2 pl-3 pr-10 text-left shadow-soft border border-gray-300",
              "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
              "text-sm"
            )}
          >
            <span className="block truncate">
              {selected ? selected.label : placeholder || "Выберите..."}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>

          <Transition
            show={open}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options
              className={clsx(
                "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5"
              )}
            >
              {options.map((option) => (
                <Listbox.Option
                  key={option.value}
                  value={option.value}
                  className={({ active }) =>
                    clsx(
                      active
                        ? "bg-primary-50 text-primary-700"
                        : "text-gray-900",
                      "relative cursor-default select-none py-2 pl-10 pr-4"
                    )
                  }
                >
                  {({ selected: isSelected }) => (
                    <>
                      <span
                        className={clsx(
                          isSelected ? "font-medium" : "font-normal",
                          "block truncate"
                        )}
                      >
                        {option.label}
                      </span>
                      {isSelected ? (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600">
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
};
